'use client';

import { PricingSwitcher } from '@/components/pricing/PricingSwitcher';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { HOST } from '@/lib/config';
import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import {
  selectErrorCode,
  selectErrorSource,
  selectIsManualOpen,
  selectScrollAnchor,
  selectShouldShowUpgradeModal,
} from '@/lib/redux/selectors/errorModalSelectors';
import { closeErrorModal } from '@/lib/redux/slices/errorModalSlice';
import type { CreditPack, PackListResponse } from '@/types/pricing';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type React from 'react';
import { useEffect, useMemo } from 'react';

const UpgradeModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const t = useTranslations('UpgradeModal');
  const locale = useLocale();
  const pathname = usePathname();
  const { isAuthenticated: isSignedIn, isLoading } = useAuth();

  // Read from Redux state
  const isOpen = useAppSelector(selectShouldShowUpgradeModal);
  const errorCode = useAppSelector(selectErrorCode);
  const isManualOpen = useAppSelector(selectIsManualOpen);
  const source = useAppSelector(selectErrorSource);
  const scrollAnchor = useAppSelector(selectScrollAnchor);

  // Auto-close when navigation lands on checkout. Kúpiť-flow keeps the modal
  // open across router.push so the underlying page doesn't flash.
  useEffect(() => {
    if (isOpen && pathname && pathname.includes('/checkout/')) {
      dispatch(closeErrorModal());
    }
  }, [isOpen, pathname, dispatch]);

  // Scroll to the caller-specified section after the modal's Dialog has
  // mounted its content. Radix portal-mounts DialogContent + runs an enter
  // animation, and nested sections (e.g. ProfessionalPricingSection) render
  // the anchor element only after their own mount — a single-frame rAF
  // races with both. Retry on each frame up to ``MAX_FRAMES`` (~250 ms at
  // 60 fps) and bail out as soon as we find the element.
  useEffect(() => {
    if (!isOpen || !scrollAnchor) return;
    const MAX_FRAMES = 15;
    let frames = 0;
    let rafId = 0;
    const tick = () => {
      const el = document.getElementById(scrollAnchor);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (++frames < MAX_FRAMES) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isOpen, scrollAnchor]);

  const handleClose = () => {
    dispatch(closeErrorModal());
  };

  // Determine which description to show based on error code
  // Only show error message if modal was NOT manually opened (i.e., it's an actual error)
  const getDescription = () => {
    if (isLoading || !isSignedIn) {
      return null;
    }

    // If user manually clicked upgrade button, don't show error message
    if (isManualOpen) {
      return null;
    }

    if (errorCode === 409) {
      return t('description409');
    }

    if (errorCode === 402) {
      return t('description402');
    }

    return t('description');
  };

  // Contextual hint based on which feature triggered the modal
  const getContextualHint = () => {
    if (source === 'export') {
      return {
        text: t('exportUpgradeHint'),
        href: null,
      };
    }
    if (source === 'spisy') {
      // Spisy file upload + indexing is paid-only (#6279); the rest of Spisy
      // (creating a case + chatting) stays free.
      return {
        text: t('spisyUpgradeHint'),
        href: null,
      };
    }
    return null;
  };

  const contextualHint = getContextualHint();

  // Client-side fetch for PAYG packs — memoized so the promise is stable
  // across re-renders (React 19's `use()` requires a stable reference).
  const packsPromise = useMemo<Promise<CreditPack[]>>(() => {
    if (!isOpen) return Promise.resolve([]);
    return fetch(`${HOST}/credit-packs/available?locale=${locale}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data: PackListResponse) =>
        Array.isArray(data) ? data : data.data
      )
      .catch(() => [] as CreditPack[]);
  }, [isOpen, locale]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='w-screen h-screen max-w-none max-h-none rounded-none overflow-y-auto bg-surface-base p-6 md:p-10'>
        <DialogHeader className='text-center space-y-2'>
          <DialogTitle className='text-center text-2xl'>
            {t('title')}
          </DialogTitle>
          <DialogDescription className='text-center'>
            {t('subtitle')}
          </DialogDescription>
          {getDescription() && (
            <DialogDescription className='text-center text-sm text-muted-foreground'>
              {getDescription()}
            </DialogDescription>
          )}
          {contextualHint && (
            <p className='text-center text-sm text-content-secondary'>
              {contextualHint.href ? (
                <Link
                  href={contextualHint.href}
                  onClick={handleClose}
                  className='text-brand-primary-color hover:underline'
                >
                  {contextualHint.text}
                </Link>
              ) : (
                <span>{contextualHint.text}</span>
              )}
            </p>
          )}
        </DialogHeader>

        <div className='mt-6'>
          <PricingSwitcher packsPromise={packsPromise} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
